/**
 * Cache de uma leitura de geolocalização por janela, compartilhado por quem precisa mandar a
 * coordenada ao servidor.
 *
 * Extraído de `contextoAuditoria` quando o portal do notificado virou o segundo consumidor. O que
 * é comum são a janela de validade, o single-flight e o teto de espera — NÃO a política de quando
 * mandar o header: a aba Auditoria a injeta no construtor de request, só em mutação; o portal a
 * pede chamada a chamada, só nas que registram acesso (inclusive `GET`, porque lá a própria
 * visualização é o evento probatório). Fundir as duas políticas num módulo só faria a regra de um
 * vazar para o outro sem ninguém decidir isso.
 *
 * Estado de módulo em vez de contexto de React porque quem consome são os construtores de
 * request, que não vivem na árvore de componentes.
 */

import type { PosicaoNavegador } from '@/shared/types/contextoAuditoria';

import { coletarPosicaoNavegador } from './geoPosicao';

const VALIDADE_DA_POSICAO_MS = 300_000;

/**
 * Teto para a request esperar uma coleta JÁ em andamento.
 *
 * Não é o timeout da coleta (esse é de 10 s, em `geoPosicao`) — é quanto uma ação do usuário
 * aceita atrasar por causa dela. Com `maximumAge` de 5 min o navegador devolve o fix em cache
 * quase sempre na hora; o teto existe para o primeiro acesso do dia, em que a permissão ainda
 * está sendo decidida e esperar 10 s seria pior que gravar "—".
 */
const ESPERA_MAXIMA_NA_REQUEST_MS = 1_200;

let posicaoEmCache: PosicaoNavegador | null = null;
let coletadaEm = 0;
/** Coleta em andamento, para a request aguardar em vez de desistir dela. */
let coletaEmVoo: Promise<PosicaoNavegador | null> | null = null;

function posicaoAindaVale(): boolean {
  return posicaoEmCache !== null && Date.now() - coletadaEm < VALIDADE_DA_POSICAO_MS;
}

/** Nova tentativa só depois da janela — permissão negada não pode virar pedido a cada clique. */
function tentativaAindaVale(): boolean {
  return coletadaEm !== 0 && Date.now() - coletadaEm < VALIDADE_DA_POSICAO_MS;
}

/**
 * Dispara a coleta uma única vez por janela e devolve a promessa em voo, para quem precisar
 * dela poder aguardar. `??=` porque dois consumidores declarando em sequência (ou uma tela que
 * remonta) não podem abrir dois pedidos de geolocalização ao navegador.
 */
function iniciarColeta(): Promise<PosicaoNavegador | null> {
  coletaEmVoo ??= coletarPosicaoNavegador().then((posicao) => {
    posicaoEmCache = posicao;
    // Carimba mesmo quando `posicao` é null: é o que faz `tentativaAindaVale` segurar a
    // próxima tentativa e impede que a negação de permissão seja repedida a cada ação.
    coletadaEm = Date.now();
    coletaEmVoo = null;
    return posicao;
  });
  return coletaEmVoo;
}

/**
 * Garante uma leitura da posição na janela corrente. Chamado ao abrir a tela que vai precisar
 * dela, nunca no meio do envio: pedi-la dentro do fluxo da request somaria até 10 s de latência
 * a uma ação do usuário.
 *
 * Permissão negada ⇒ resolve mesmo assim; quem consome trata a ausência como "sem coordenada".
 */
export async function declararColetaDePosicao(): Promise<void> {
  if (posicaoAindaVale() || tentativaAindaVale()) return;

  await iniciarColeta();
}

/**
 * A posição da janela corrente, ou `null`.
 *
 * A validade é conferida AQUI, e não só na coleta: quem fica horas na mesma tela não passa por
 * `declararColetaDePosicao` de novo, e o evento é imutável por trigger — registrar a posição de
 * onde a pessoa estava de manhã num acesso da noite é pior do que registrar "—", porque o erro
 * fica gravado como fato e não se corrige.
 *
 * Aguarda uma coleta em voo com teto: a declaração roda com `void` no efeito da tela e a leitura
 * do navegador leva de centenas de ms a 10 s, então quem age logo em seguida veria o header sair
 * sem coordenada.
 */
export async function posicaoDaJanelaCorrente(): Promise<PosicaoNavegador | null> {
  if (!posicaoAindaVale() && coletaEmVoo) {
    // `clearTimeout` no `finally` porque o perdedor do `race` continua vivo: sem isto cada
    // chamada que corre deixa um timer pendente, e agora quem chama é toda ação do portal que
    // registra acesso, não só a mutação ocasional da aba Auditoria.
    let expiracao: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        coletaEmVoo,
        new Promise((resolve) => {
          expiracao = setTimeout(resolve, ESPERA_MAXIMA_NA_REQUEST_MS);
        }),
      ]);
    } finally {
      clearTimeout(expiracao);
    }
  }

  return posicaoAindaVale() ? posicaoEmCache : null;
}
